/**
 * estimateModel — SINGLE SOURCE OF TRUTH for the admission cost-sheet estimate
 * (S6 + S9). Every surface that shows an estimated total — the AdmitPatientForm
 * rail (FE-1), the standalone EstimatePanel (FE-5) and the desk "Edit plan /
 * estimate" dialog — consumes THIS pure selector. Never re-derive the grand
 * total inline per surface (memory rule: single source of truth for derived
 * values), so the desktop rail and the dialog can't diverge.
 *
 * Pure client-side math over already-fetched snapshots (bed tariff + catalog
 * prices). ZERO network — the server re-snapshots authoritatively at submit /
 * plan-estimate save; this is only the live preview.
 *
 * NOTE: this is DISPLAY ONLY. It never creates a charge/ledger row or an OT
 * booking (S6/S9 zero-money guard); it just computes a number.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {object} args
 * @param {number|null} args.expectedStayDays   expected LOS (days); null/0 → no bed-day lines
 * @param {object|null} args.bedCategory         { per_day_charge, nursing_charge_per_day, rmo_charge_per_day, name, bed_category_id } | null (emergency / no bed)
 * @param {Array}       args.plannedInterventions [{ price_snapshot }] — snapshot prices, GST-inclusive display
 * @param {Array}       args.otherLines           [{ label, amount }] free-text manual lines
 * @returns {{
 *   bedDaysAmount: number, nursingAmount: number, rmoAmount: number,
 *   interventionsAmount: number, otherAmount: number, grandTotal: number,
 *   lines: Array<{ key, label, amount }>,   // itemized, in display order
 * }}
 */
export function computeEstimate({
  expectedStayDays,
  bedCategory,
  plannedInterventions,
  otherLines,
  plannedCategory = false, // S10: bedCategory is a PLANNED category (no bed yet) → label it so
  bedTbd = false,          // S10: no bed + no planned category → show a "Bed — to be assigned" ₹0 line
} = {}) {
  const days = Math.max(0, Math.trunc(num(expectedStayDays)));
  const cat = bedCategory || null;

  const perDay = cat ? num(cat.per_day_charge) : 0;
  const nursingPerDay = cat ? num(cat.nursing_charge_per_day) : 0;
  const rmoPerDay = cat ? num(cat.rmo_charge_per_day) : 0;

  const bedDaysAmount = perDay * days;
  const nursingAmount = nursingPerDay * days;
  const rmoAmount = rmoPerDay * days;

  const interventions = Array.isArray(plannedInterventions) ? plannedInterventions : [];
  const interventionsAmount = interventions.reduce((s, l) => s + num(l && l.price_snapshot), 0);

  const others = Array.isArray(otherLines) ? otherLines : [];
  const otherAmount = others.reduce((s, l) => s + num(l && l.amount), 0);

  const lines = [];
  if (cat && days > 0) {
    const catName = cat.name || cat.bed_category_name || 'Bed';
    const bedLabel = plannedCategory
      ? `Planned category — ${catName} × ${days} day${days === 1 ? '' : 's'}`
      : `Bed — ${catName} × ${days} day${days === 1 ? '' : 's'}`;
    lines.push({ key: 'bed', label: bedLabel, amount: bedDaysAmount });
    if (nursingPerDay > 0) lines.push({ key: 'nursing', label: `Nursing charge × ${days} day${days === 1 ? '' : 's'}`, amount: nursingAmount });
    if (rmoPerDay > 0) lines.push({ key: 'rmo', label: `RMO charge × ${days} day${days === 1 ? '' : 's'}`, amount: rmoAmount });
  } else if (bedTbd) {
    // No bed and no planned category yet — a visible ₹0 placeholder so the sheet
    // still shows the pending bed cost (never a silent gap).
    lines.push({ key: 'bed-tbd', label: 'Bed — to be assigned', amount: 0 });
  }
  if (interventions.length > 0) {
    lines.push({ key: 'interventions', label: `Planned interventions (${interventions.length})`, amount: interventionsAmount });
  }
  others.forEach((l, i) => {
    if (l && l.label) lines.push({ key: `other-${i}`, label: l.label, amount: num(l.amount) });
  });

  const grandTotal = bedDaysAmount + nursingAmount + rmoAmount + interventionsAmount + otherAmount;

  return { bedDaysAmount, nursingAmount, rmoAmount, interventionsAmount, otherAmount, grandTotal, lines };
}

/** ₹ formatter (Indian grouping) — one place so every surface formats alike. */
export function formatRupees(amount) {
  const n = num(amount);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
